<template>
  <SkeletonDataTable
    :id="id"
    v-model:expanded-rows="expandedRows"
    :value="spotMarketsAndBids"
    :loading="spotMarkets.loading"
    :row-class="getRowClass"
    class="spot-market-table elevated"
    collapsed-row-icon="pi pi-chevron-down"
    expanded-row-icon="pi pi-chevron-up"
    tabindex="-1"
    @row-expand="onRowExpand($event.data)"
  >
    <template v-if="slots.empty" #empty>
      <slot name="empty" />
    </template>

    <Column
      :header="t('carriers.carriers')"
      class="carrier-column"
      field="carrier.scac"
    >
      <template #body="{ data }: { data: RateQuote }">
        <RateQuoteCarrier :rate-quote="data" with-context-chat />

        <RateQuoteWarnings
          v-if="
            data.id && data.id > 0 // negative id represents a mapped spot market
          "
          :rate-quote="data"
          @click="onChipClicked(data)"
        />
      </template>
    </Column>

    <Column :header="t('carriers.modes')" class="w-1" field="mode">
      <template #body="{ data }: { data: RateQuote }">
        {{ data.mode ? translateEnum(data.mode, 'Mode') : '-' }}

        <div v-if="data.equipmentType" class="text-gray-darker sub-heading">
          {{ data.equipmentType.name }}
        </div>
      </template>
    </Column>

    <Column :header="t('rateShop.service')" class="w-1">
      <template #body="{ data }: { data: RateQuote }">
        {{ data.serviceType ? titleCase(data.serviceType) : '-' }}

        <div class="text-gray-darker sub-heading">
          {{ renderServiceInfo(data) }}
        </div>
      </template>
    </Column>

    <Column :header="t('rateShop.transit')" class="w-1">
      <template #body="{ data }: { data: RateQuote }">
        {{
          data.transitTime
            ? t('rateShop.countDays', { count: data.transitTime })
            : '-'
        }}
      </template>
    </Column>

    <Column :header="t('common.dateSent')" class="date-column">
      <template #body="{ data }: { data: RateQuote }">
        <div class="flex flex-column">
          {{ renderDateTime(data.ratedDate, { timeFormat: 'NONE' }) }}

          <span
            v-if="
              data.id &&
              data.id < 0 && // negative id represents a mapped spot market
              data.quoteExpiration
            "
            v-tooltip="renderDateTime(data.quoteExpiration)"
            class="text-gray-darker sub-heading"
          >
            {{
              data.quoteExpiration.getTime() < Date.now()
                ? t('common.expired')
                : t('common.expires')
            }}

            <TimeAgo v-slot="{ timeAgo }" :time="data.quoteExpiration">
              {{ timeAgo }}
            </TimeAgo>
          </span>
        </div>
      </template>
    </Column>

    <Column :header="t('common.status')">
      <template #body="{ data }: { data: RateQuote }">
        <SpotMarketStatusChip
          v-if="
            data.id && data.id < 0 // negative id represents a mapped spot market
          "
          :status="getSpotMarketStatus(data)"
        />
        <span v-else class="text-success font-medium">
          {{
            renderMoney(data.totalPrice, {
              currencyCode: data.currencyCode,
            })
          }}
        </span>
      </template>
    </Column>

    <Column class="select-rate-column">
      <template #body="{ data }: { data: RateQuote }">
        <SpotMarketActionButton
          :selectable="isRateQuoteSelectable(data)"
          :dispatchable="isRateQuoteDispatchable(data)"
          :cancellable="canCancelSpotMarket(data)"
          :resendable="canResendSpotMarket(data)"
          :loading="spotMarkets.loading"
          @select-rate="onRateSelected(data)"
          @cancel-spot-market="onSpotMarketCancelled(data)"
          @resend-spot-market="onSpotMarketResent(data)"
        />
      </template>
    </Column>

    <Column
      :header-style="{ width: '3rem', 'min-width': '3rem' }"
      :pt="{
        rowToggleButton: rowToggleButtonPT,
      }"
      expander
    />

    <template #expansion="{ data }: { data: RateQuote }">
      <RateQuoteExpandedDetails
        v-if="
          data.id && data.id > 0 // negative id represents a mapped spot market
        "
        :active-tab-key="rateQuoteToTabKeyMap.get(data.id!)"
        :rate-quote="data"
      />
    </template>
  </SkeletonDataTable>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { UseTimeAgo as TimeAgo } from '@vueuse/components';
import { useConfirm } from 'primevue/useconfirm';
import type { PropType, Ref } from 'vue';
import { computed, nextTick, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import {
  useExpandedRows,
  useTooltip,
  useUid,
} from '@rygen/rygen-ui/composables';
import {
  lowerCase,
  renderDateTime,
  renderMoney,
  titleCase,
  translateEnum,
} from '@rygen/rygen-ui/i18n';
import { Loadable } from '@rygen/rygen-ui/model';

import { useDoNotDispatch } from '@/composables';
import { RateQuote, SpotMarket, SpotMarketStatus } from '@/model';
import {
  isCompletedStatus,
  isResendableStatus,
} from '@/model/carrierPortal/SpotMarketStatus';

import SpotMarketStatusChip from '@/components/carrierPortal/rateShop/SpotMarketStatusChip.vue';
import RateQuoteExpandedDetails, {
  TabKey,
} from '@/components/rateShop/expandedDetails/RateQuoteExpandedDetails.vue';
import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RateQuoteWarnings from '@/components/rateShop/RateQuoteWarnings.vue';
import SpotMarketActionButton from '@/components/rateShop/SpotMarketActionButton.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';

const props = defineProps({
  id: {
    type: String,
    default: () => useUid('rate-shop-table').uid,
  },
  spotMarkets: {
    type: Object as PropType<Loadable<SpotMarket[]>>,
    required: true,
  },
  selectable: {
    type: Boolean,
    default: () => false,
  },
});
const { id, spotMarkets, selectable } = toRefs(props);
const emit = defineEmits<{
  (event: 'rate-selected', rateQuote: RateQuote): void;
  (event: 'spot-market-cancelled', spotMarketId: number): void;
  (event: 'spot-market-resent', spotMarketId: number): void;
}>();

const slots = defineSlots<{
  empty?: () => any;
}>();

const confirm = useConfirm();
const { isRateQuoteDispatchable } = useDoNotDispatch();
const { t } = useI18n();
const vTooltip = useTooltip();

const spotMarketsAndBids = computed(() => {
  const spotMarketEntities = spotMarkets.value.data ?? [];
  return spotMarketEntities
    .map((s) => (s.bids.length ? s.bids : s))
    .flat()
    .map((spotMarketOrBid: SpotMarket | RateQuote) => {
      return spotMarketOrBid instanceof RateQuote
        ? spotMarketOrBid
        : new RateQuote({
            // Use negative ids to signify this RateQuote is actually a spot market
            id: spotMarketOrBid.id ? spotMarketOrBid.id * -1 : undefined,
            rateStatus: spotMarketOrBid.status,
            carrier: spotMarketOrBid.carrier,
            mode: spotMarketOrBid.mode,
            equipmentType: spotMarketOrBid.equipmentType,
            // serviceType on RateQuote is a human readable string, not natural id
            serviceType: spotMarketOrBid.serviceType?.name,
            ratedDate: spotMarketOrBid.createdDate?.toJSDate(),
            createdBy: spotMarketOrBid.createdBy,
            quoteExpiration: spotMarketOrBid.expirationDate?.toJSDate(),
          });
    });
});

const bidToSpotMarketMap = computed(() => {
  const spotMarketList = spotMarkets.value.data ?? [];
  return spotMarketList.reduce(addBidsToMap, new Map<number, SpotMarket>());
});

const { expandedRows, getRowClass } = useExpandedRows(spotMarketsAndBids, {
  expandIfSingleItem: false,
});

const rateQuoteToTabKeyMap: Ref<Map<number, TabKey | undefined>> = ref(
  new Map(),
);

const canSelect = computed(() => {
  return (
    selectable.value && authorization.hasAuthority('rateShop', 'selectRate')
  );
});

const rowToggleButtonPT = ({
  instance: { rowData: spotMarketOrBid },
}: {
  instance: { rowData: RateQuote };
}) => ({
  class: {
    hidden: !spotMarketOrBid.id || spotMarketOrBid.id < 0,
  },
});

const renderServiceInfo = (rateQuote: RateQuote) => {
  return [rateQuote.serviceTypeDescription, rateQuote.service]
    .filter((x) => x)
    .map((x) => titleCase(x))
    .join(' • ');
};

const onRowExpand = (rateQuote: RateQuote) => {
  if (rateQuote.id) {
    rateQuoteToTabKeyMap.value.set(rateQuote.id, undefined);
  }
};

const onChipClicked = (rateQuote: RateQuote) => {
  const isRowExpanded = expandedRows.value.includes(rateQuote);

  if (!isRowExpanded) {
    expandedRows.value.push(rateQuote);
  }

  nextTick(() =>
    rateQuoteToTabKeyMap.value.set(rateQuote.id!, 'OTHER_DETAILS'),
  );
};

/**
 * Get the status of the spot market represented by the incoming `rateQuote`
 *
 * This function is only needed because doing Typescript casts in a `template`
 * messes up VS Code's syntax highlighting.
 */
const getSpotMarketStatus = (rateQuote: RateQuote) => {
  return rateQuote.rateStatus as SpotMarketStatus | undefined;
};

const addBidsToMap = (map: Map<number, SpotMarket>, spotMarket: SpotMarket) => {
  spotMarket.bids.forEach(({ id }) => {
    if (id != null) {
      map.set(id, spotMarket);
    }
  });
  return map;
};

const canResendSpotMarket = (rateQuote: RateQuote): boolean => {
  if (!rateQuote.id) {
    return false;
  }
  if (rateQuote.id < 0) {
    return isResendableStatus(getSpotMarketStatus(rateQuote));
  }
  const spotMarket = bidToSpotMarketMap.value.get(rateQuote.id);
  return spotMarket?.isResendable ?? false;
};

const canCancelSpotMarket = (rateQuote: RateQuote): boolean => {
  if (!rateQuote.id) {
    return false;
  }

  if (rateQuote.id < 0) {
    const status = getSpotMarketStatus(rateQuote);
    return !isCompletedStatus(status) && status !== SpotMarketStatus.SENDING;
  }

  const spotMarket = bidToSpotMarketMap.value.get(rateQuote.id);
  return spotMarket?.isCancellable ?? false;
};

const isRateQuoteSelectable = (rateQuote: RateQuote): boolean => {
  return canSelect.value && !!rateQuote.id && rateQuote.id > 0;
};

const onSpotMarketCancelled = (rateQuote: RateQuote) => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('common.cancelParam', {
      param: t('carrierPortal.spotMarket.spotMarkets', 1),
    }),
    message: t('common.confirmCancelationParams', {
      param: t('carrierPortal.spotMarket.spotMarkets', 1),
    }),
    accept: () => {
      const spotMarketId = resolveSpotMarketId(rateQuote);
      if (spotMarketId) {
        emit('spot-market-cancelled', spotMarketId);
      }
    },
    acceptClass: 'p-button-sm p-button-danger',
    acceptIcon: 'pi pi-times',
    acceptProps: { iconPos: 'right' },
    acceptLabel: t('common.cancelParam', {
      param: t('carrierPortal.spotMarket.spotMarkets', 1),
    }),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.dontCancelParam', {
      param: t('carrierPortal.spotMarket.spotMarkets', 1),
    }),
  });
};

const resolveSpotMarketId = (rateQuote: RateQuote): number | undefined => {
  if (!rateQuote.id) {
    return undefined;
  }
  if (rateQuote.id < 0) {
    return Math.abs(rateQuote.id);
  }
  return bidToSpotMarketMap.value.get(rateQuote.id)?.id;
};

const onSpotMarketResent = (rateQuote: RateQuote) => {
  confirm.require({
    group: 'rygen-confirm-dialog',
    header: t('carrierPortal.spotMarket.resendSpotMarketInvite'),
    message: t('carrierPortal.spotMarket.confirmResendSpotMarket'),
    accept: () => {
      const spotMarketId = resolveSpotMarketId(rateQuote);
      if (spotMarketId) {
        emit('spot-market-resent', spotMarketId);
      }
    },
    acceptClass: 'p-button-sm',
    acceptIcon: PrimeIcons.SEND,
    acceptProps: { iconPos: 'right' },
    acceptLabel: t('carrierPortal.spotMarket.resendSpotMarketInvite'),
    rejectClass: 'p-button-sm p-button-text',
    rejectLabel: t('common.cancel'),
  });
};

const onRateSelected = (rateQuote: RateQuote) => {
  if (rateQuote.doNotDispatch) {
    confirm.require({
      group: 'rygen-confirm-dialog',
      header: t('rateShop.doNotDispatch.selectRateHeader'),
      message: t('rateShop.doNotDispatch.entityNameConfirmOverride', {
        entityName: lowerCase(t('carriers.carriers')),
      }),
      accept: () => emit('rate-selected', rateQuote),
      acceptClass: 'p-button-sm p-button-warning',
      acceptIcon: 'pi pi-check',
      acceptLabel: t('rateShop.select'),
      rejectClass: 'p-button-sm p-button-text',
      rejectLabel: t('common.cancel'),
    });
  } else {
    emit('rate-selected', rateQuote);
  }
};
</script>

<style>
.spot-market-table {
  .p-datatable.p-component {
    .p-datatable-table {
      .datatable-row {
        .sub-heading {
          font-size: 12px;
        }

        .rate-quote-warnings {
          margin-top: var(--sp-3);
        }
      }

      .p-datatable-row-expansion {
        td {
          padding: var(--sp-4);
        }
      }
    }
  }
}
</style>
